import { AppStore } from "../../../stores/app-store.service";
import { Subscription } from "rxjs";
import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { NotificationService } from "../../../services/notification.service";
import { FilterUrlSyncService } from "../../../services/filter-url-sync.service";
import { KPI_FILTER_CONFIG } from "../../../services/filter-sync-configs";

import { IKpiAlertSites } from "psa-tnt-models";
import { ContainerTypeDTO } from "../../../models/container-type";
import { IKpiBatteryLevelsDTO, IKpiStateColorsDTO } from "psa-tnt-models";

import { DeviceContainerStateControllerService } from "../../../services/device-container-state-controller.service";
import { SidenavFiltersComponent } from "../../sidenav/sidenav-filters/sidenav-filters.component";
import { HeaderKpiComponent } from "./header-kpi/header-kpi.component";
import { GraphKpiComponent } from "./graph-kpi/graph-kpi.component";

@Component({
  selector: "app-kpi",
  templateUrl: "./kpi.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./kpi.component.scss"],
  imports: [SidenavFiltersComponent, HeaderKpiComponent, GraphKpiComponent]
})
export class KpiComponent implements OnInit, OnDestroy {
  private subscription: Subscription = new Subscription();

  private readonly notifier: NotificationService;

  public allContainersTypes: ContainerTypeDTO[];
  public filterContainers: string[] = [];
  public filterTorn: string;
  public filterUnknown: string;
  public filterDevicesTypes: string[] = [];
  public filterContainerCategory: string[] = [];

  public alertSites: IKpiAlertSites[] = [];
  public statusIndicators: IKpiStateColorsDTO;
  public batteryIndicators: IKpiBatteryLevelsDTO;
  public loading = {
    stateColors: true,
    battery: true,
    alertSites: true
  };

  constructor(
    private store: AppStore,
    private deviceContainerStateControllerService: DeviceContainerStateControllerService,
    private notifierService: NotificationService,
    private route: ActivatedRoute,
    private filterSync: FilterUrlSyncService
  ) {
    this.notifier = notifierService;
  }

  ngOnInit() {
    // The URL wins over localStorage (a shared link has to render what it says):
    // hydrate first, then take every field from the decoded values. hydrateFromUrl
    // has already written them to localStorage, so re-reading that here would only
    // decode the same thing a second time.
    const hydrated = this.filterSync.hydrateFromUrl(
      this.route,
      KPI_FILTER_CONFIG
    );
    this.filterContainers = hydrated["pkg"];
    this.filterContainerCategory = hydrated["pkgcat"];
    this.filterDevicesTypes = hydrated["trktyp"];
    // The two radios always have a value: the config declares their base one, so
    // the decoded value is never empty and never needs a fallback here.
    this.filterTorn = hydrated["torn"];
    this.filterUnknown = hydrated["unk"];
    this.mirrorFilters();
    this.initStore();
    this.refreshData();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  /**
   * Publish the page's WHOLE filter set to the URL and to localStorage, empty ones
   * included (`?pkg=&trktyp=&torn=noTorn&unk=noUnknown`): the address bar is the
   * editable form of what is displayed, so every filter has to be visible in it as
   * a key to type into. The service reads nothing back, so this has to list every
   * param KPI_FILTER_CONFIG declares — and it is the only thing that writes them.
   */
  private mirrorFilters(): void {
    this.filterSync.mirrorToUrl(this.route, KPI_FILTER_CONFIG, {
      pkg: this.filterContainers,
      pkgcat: this.filterContainerCategory,
      trktyp: this.filterDevicesTypes,
      torn: this.filterTorn,
      unk: this.filterUnknown
    });
  }

  private initStore(): void {
    this.subscription.add(
      this.store.ctTypes$.subscribe((containers: ContainerTypeDTO[]) => {
        this.allContainersTypes = containers;
        this.refreshData();
      })
    );
  }

  private refreshData() {
    if (
      this.allContainersTypes.length !== 0 &&
      this.deviceContainerStateControllerService.getStateColorsIndicator({
        requestId: 0,
        filterContainers: this.filterContainers,
        filterContainerCategory: this.filterContainerCategory,
        containerTypes: this.allContainersTypes
      }) !== null
    ) {
      this.subscription.add(
        this.deviceContainerStateControllerService
          .getStateColorsIndicator({
            requestId: 0,
            filterContainers: this.filterContainers,
            filterContainerCategory: this.filterContainerCategory,
            filterTorn: this.filterTorn,
            filterUnknown: this.filterUnknown,
            filterDevicesTypes: this.filterDevicesTypes,
            containerTypes: this.allContainersTypes
          })
          .subscribe(
            (kpi: IKpiStateColorsDTO) => {
              this.statusIndicators = kpi;
            },
            () => {
              this.notifier.error("State Colors indicators request failed.");
            },
            () => {
              this.loading.stateColors = false;
            }
          )
      );

      this.subscription.add(
        this.deviceContainerStateControllerService
          .getBatteryColors({
            requestId: 0,
            filterContainers: this.filterContainers,
            filterContainerCategory: this.filterContainerCategory,
            filterTorn: this.filterTorn,
            filterUnknown: this.filterUnknown,
            filterDevicesTypes: this.filterDevicesTypes,
            containerTypes: this.allContainersTypes
          })
          .subscribe(
            (kpi: IKpiBatteryLevelsDTO) => {
              this.batteryIndicators = kpi;
            },
            () => {
              this.notifier.error("Battery indicators request failed.");
            },
            () => {
              this.loading.battery = false;
            }
          )
      );

      // Alerts Sites Indicator - TMP DISABLE
      // this.subscription.add(
      //   this.deviceContainerStateControllerService
      //     .getAlertSites({
      //       requestId: 0,
      //       filterContainers: this.filterContainers,
      //       containerTypes: this.allContainersTypes
      //     })
      //     .subscribe(
      //       (alertSites: IKpiAlertSites[]) => {
      //         this.alertSites = alertSites;
      //       },
      //       () => {
      //         this.notifier.notify(
      //           "error",
      //           "Alerts By Sites indicators request failed."
      //         );
      //       },
      //       () => {
      //         this.loading.alertSites = false;
      //       }
      //     )
      // );
    }
  }

  public changeContainers(containers: string[]) {
    this.filterContainers = containers;
    this.mirrorFilters();
    this.refreshData();
  }

  public changeTorn(torn: string) {
    this.filterTorn = torn;
    this.mirrorFilters();
    this.refreshData();
  }

  public changeUnknown(unknown: string) {
    this.filterUnknown = unknown;
    this.mirrorFilters();
    this.refreshData();
  }

  public changeDevicesTypes(devicesTypes: string[]) {
    this.filterDevicesTypes = devicesTypes;
    this.mirrorFilters();
    this.refreshData();
  }

  public changeContainerCategory(categories: string[]) {
    this.filterContainerCategory = categories;
    this.mirrorFilters();
    this.refreshData();
  }

  public getObjKeys(obj: object): string[] {
    return Object.keys(obj).filter((key) => key !== "total");
  }
}
